import { create } from 'zustand';
import { confirmDialog } from '@/components/ConfirmDialog';
import { hasUnsavedChanges, unsavedLeaveMessage } from '@/utils/unsavedChanges';
import { canOpenInDialog } from './link-targets';

/** A factory page open in the link dialog, over a page in `baseSlug`'s project (null: outside projects). */
export interface OpenPage {
  path: string;
  baseSlug: string | null;
  /** A new one for each link, so another link starts the dialog afresh at its page. */
  key: number;
}

interface LinkDialogState {
  page: OpenPage | null;
  /** A web address open in the framed dialog (over the page dialog, when that's open too). */
  web: string | null;
}

export const useLinkDialogStore = create<LinkDialogState>()(() => ({ page: null, web: null }));

let nextKey = 1;

/** Whether the page in the dialog has unsaved work (set by PageLinkDialog while it's open). */
let dialogDirty: (() => boolean) | null = null;

export function setPageDialogDirtyCheck(check: (() => boolean) | null): void {
  dialogDirty = check;
}

/** Before the dialog's page goes (Close, Escape, Back, another link): asks when it has unsaved work. Resolves to whether to go. */
export function isPageDialogDirty(): boolean {
  return Boolean(dialogDirty?.());
}

/**
 * Before work is let go: the dialog's page's (Close, Escape, Back, another link), or with `everything`,
 * the page underneath's too (Go to this page leaves both). Asks once when any of it is unsaved.
 */
export async function confirmLeavePageDialog({ everything = false }: { everything?: boolean } = {}): Promise<boolean> {
  if (!(everything ? hasUnsavedChanges('leave') : dialogDirty?.())) return true;
  return confirmDialog({
    title: 'Leave this page?',
    description: unsavedLeaveMessage() ?? 'You have unsaved changes. Leave without saving?',
    confirmLabel: 'Leave',
  });
}

/**
 * The project of the page under the link dialog, for a link about to open in it: while a dialog is
 * open, the one it was opened over (its own project may be the tab's for now, SF-T-160 review).
 */
export function linkDialogBaseSlug(current: string | null): string | null {
  const page = useLinkDialogStore.getState().page;
  return page ? page.baseSlug : current;
}

/** Opens a page in the dialog, in place of one already there once its unsaved work is let go. */
export async function openPageDialog(path: string, baseSlug: string | null): Promise<void> {
  if (useLinkDialogStore.getState().page && !(await confirmLeavePageDialog())) return;
  useLinkDialogStore.setState({ page: { path, baseSlug, key: nextKey++ } });
}

/**
 * A factory page to show from code, in the chat (a toast's Open): in the link dialog where it can show
 * there, else through `navigate`. Over a page in `currentSlug`'s project, as canOpenInDialog says.
 */
export function openInLinkDialog(path: string, currentSlug: string | null, navigate: (to: string) => void): void {
  const baseSlug = linkDialogBaseSlug(currentSlug);
  if (canOpenInDialog(path, baseSlug)) void openPageDialog(path, baseSlug);
  else navigate(path);
}

export function closePageDialog(): void {
  useLinkDialogStore.setState({ page: null });
}

/** Close, Escape or a click outside: asks first when the dialog's page has unsaved work. */
export async function requestClosePageDialog(): Promise<void> {
  if (await confirmLeavePageDialog()) closePageDialog();
}

export function openWebDialog(url: string): void {
  useLinkDialogStore.setState({ web: url });
}

export function closeWebDialog(): void {
  useLinkDialogStore.setState({ web: null });
}
